gsap.registerPlugin(ScrollTrigger);

import { initBannerImage } from './banner-image.js';
import { initInfoCenterText } from './info-center-text.js';
import { initParallaxBlocks } from './parallax.js';
import { initStoriesGridVideo } from './stories-grid-video.js';
import { initSmoothScroll } from './smooth-scroll.js';
import { initPreloader } from './preloader.js';
import { initCardSwipers } from './card-swipers.js';

initSmoothScroll();

function initBurgerMenu() {
    const root = document.querySelector('[data-menu]');

    if (!root) {
        return;
    }

    const openButton = root.querySelector('[data-menu-open]');
    const closeButton = root.querySelector('[data-menu-close]');
    const panel = root.querySelector('[data-menu-panel]');
    const closeOnClick = panel?.querySelectorAll('[data-menu-close-on-click]');

    if (!openButton || !closeButton || !panel) {
        return;
    }

    const openMenu = () => {
        panel.classList.remove(
            'opacity-0',
            '-translate-y-2',
            'pointer-events-none',
        );
        panel.classList.add('opacity-100', 'translate-y-0');
        openButton.classList.add('hidden');
        closeButton.classList.remove('hidden');
        openButton.setAttribute('aria-expanded', 'true');
        panel.setAttribute('aria-hidden', 'false');
        document.body.classList.add('overflow-hidden');
    };

    requestAnimationFrame(() => {
        panel.classList.remove('transition-none');
        panel.classList.add('transition-all');
    });

    const closeMenu = () => {
        panel.classList.add(
            'opacity-0',
            '-translate-y-2',
            'pointer-events-none',
        );
        panel.classList.remove('opacity-100', 'translate-y-0');
        closeButton.classList.add('hidden');
        openButton.classList.remove('hidden');
        openButton.setAttribute('aria-expanded', 'false');
        panel.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('overflow-hidden');
    };

    openButton.addEventListener('click', openMenu);
    closeButton.addEventListener('click', closeMenu);
    closeOnClick?.forEach((item) => {
        item.addEventListener('click', closeMenu);
    });

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeMenu();
        }
    });
}

initBurgerMenu();
initCardSwipers();



function initDropdowns() {
    // 1. Handle Clicks (Trigger, Option, or Outside)
    document.addEventListener('click', (event) => {
        const target = event.target;

        // --- CASE A: Clicked a Trigger ---
        const trigger = target.closest('[data-dropdown-trigger]');
        if (trigger) {
            const root = trigger.closest('[data-dropdown]');
            if (!root) return;
            
            const menu = root.querySelector('[data-dropdown-menu]');
            const icon = root.querySelector('[data-dropdown-icon]');
            
            // Close all OTHER dropdowns first (optional, but good UX)
            closeAllDropdowns(menu); 

            // Toggle current
            const isClosed = menu.classList.contains('pointer-events-none');
            if (isClosed) {
                openMenu(menu, trigger, icon);
            } else {
                closeMenu(menu, trigger, icon);
            }
            return; // Stop here so we don't trigger the "outside click" logic
        }

        // --- CASE B: Clicked an Option ---
        const option = target.closest('[data-dropdown-option]');
        if (option) {
            if (option.disabled) return;

            const root = option.closest('[data-dropdown]');
            const label = root?.querySelector('[data-dropdown-label]');
            const menu = root?.querySelector('[data-dropdown-menu]');
            const trigger = root?.querySelector('[data-dropdown-trigger]');
            const icon = root?.querySelector('[data-dropdown-icon]');
            
            // Update Label
            const value = option.textContent?.trim();
            if (value && label) {
                label.textContent = value;
            }

            // Close Menu
            if (menu && trigger) {
                closeMenu(menu, trigger, icon);
            }
            return;
        }

        // --- CASE C: Clicked Outside ---
        // If we clicked something that isn't a dropdown, close all open menus
        if (!target.closest('[data-dropdown]')) {
            closeAllDropdowns();
        }
    });

    // 2. Handle Escape Key
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closeAllDropdowns();
        }
    });
}

// Helper: Open
function openMenu(menu, trigger, icon) {
    menu.classList.remove('transition-none');
    menu.classList.add('transition-all');
    
    menu.classList.remove('opacity-0', 'scale-95', 'pointer-events-none');
    menu.classList.add('opacity-100', 'scale-100');
    trigger.setAttribute('aria-expanded', 'true');
    
    if (icon) icon.classList.add('rotate-180');
}

// Helper: Close
function closeMenu(menu, trigger, icon) {
    menu.classList.add('opacity-0', 'scale-95', 'pointer-events-none');
    menu.classList.remove('opacity-100', 'scale-100');
    trigger.setAttribute('aria-expanded', 'false');
    
    if (icon) icon.classList.remove('rotate-180');
}

function closeAllDropdowns(exceptionMenu = null) {
    const allMenus = document.querySelectorAll('[data-dropdown-menu]');
    
    allMenus.forEach(menu => {
        if (menu === exceptionMenu) return; // Don't close the one we just clicked
        
        // Find the specific trigger/icon for this menu to update their state
        const root = menu.closest('[data-dropdown]');
        const trigger = root?.querySelector('[data-dropdown-trigger]');
        const icon = root?.querySelector('[data-dropdown-icon]');

        if (trigger) {
            closeMenu(menu, trigger, icon);
        }
    });
}

// Run once
initDropdowns();

function initPopups() {
    const popups = document.querySelectorAll('[data-popup]');

    if (!popups.length) {
        return;
    }

    popups.forEach((popup) => {
        const name = popup.getAttribute('data-popup');
        const panel = popup.querySelector('[data-popup-panel]');

        if (!name) {
            return;
        }

        const openers = document.querySelectorAll(
            `[data-popup-open="${name}"]`,
        );
        const closers = popup.querySelectorAll('[data-popup-close]');

        const isClosed = () => popup.classList.contains('pointer-events-none');
        const setInert = (state) => {
            // popup.inert = state;
        };

        const openPopup = () => {
            popup.classList.remove('opacity-0', 'pointer-events-none');
            popup.setAttribute('aria-hidden', 'false');
            setInert(false);
            document.body.classList.add('overflow-hidden');
            panel?.classList.remove('translate-x-full');
        };

        const closePopup = () => {
            popup.classList.add('opacity-0', 'pointer-events-none');
            popup.setAttribute('aria-hidden', 'true');
            setInert(true);
            document.body.classList.remove('overflow-hidden');
            panel?.classList.add('translate-x-full');
        };

        requestAnimationFrame(() => {
            popup.classList.remove('transition-none');
            popup.classList.add('transition-opacity');
        });

        if (popup.getAttribute('aria-hidden') !== 'false') {
            setInert(true);
        }

        openers.forEach((opener) => {
            opener.addEventListener('click', (event) => {
                event.preventDefault();
                openPopup();
            });
        });

        closers.forEach((closer) => {
            closer.addEventListener('click', closePopup);
        });

        popup.addEventListener('click', (event) => {
            if (event.target === popup) {
                closePopup();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !isClosed()) {
                closePopup();
            }
        });
    });
}

function initAccordions() {
    const accordions = document.querySelectorAll('[data-accordion]');

    if (!accordions.length) {
        return;
    }

    accordions.forEach((accordion) => {
        const items = Array.from(
            accordion.querySelectorAll('[data-accordion-item]'),
        );

        if (!items.length) {
            return;
        }

        const openItem = (item) => {
            const panel = item.querySelector('[data-accordion-panel]');
            const trigger = item.querySelector('[data-accordion-trigger]');
            const icon = item.querySelector('[data-accordion-icon]');

            if (panel) {
                panel.classList.remove('hidden');
                panel.style.maxHeight = `${panel.scrollHeight}px`;
            }
            trigger?.setAttribute('aria-expanded', 'true');
            panel?.setAttribute('aria-hidden', 'false');
            icon?.classList.add('rotate-180');
        };

        const closeItem = (item) => {
            const panel = item.querySelector('[data-accordion-panel]');
            const trigger = item.querySelector('[data-accordion-trigger]');
            const icon = item.querySelector('[data-accordion-icon]');

            if (panel) {
                panel.style.maxHeight = '0px';
            }
            trigger?.setAttribute('aria-expanded', 'false');
            panel?.setAttribute('aria-hidden', 'true');
            icon?.classList.remove('rotate-180');
        };

        items.forEach((item) => {
            const trigger = item.querySelector('[data-accordion-trigger]');
            if (!trigger) {
                return;
            }

            trigger.addEventListener('click', () => {
                const isOpen = trigger.getAttribute('aria-expanded') === 'true';

                if (isOpen) {
                    closeItem(item);
                    return;
                }

                items.forEach((other) => {
                    if (other !== item) {
                        closeItem(other);
                    }
                });
                openItem(item);
            });
        });

        window.addEventListener('resize', () => {
            items.forEach((item) => {
                const trigger = item.querySelector('[data-accordion-trigger]');
                const panel = item.querySelector('[data-accordion-panel]');

                if (!trigger || !panel) {
                    return;
                }

                if (trigger.getAttribute('aria-expanded') === 'true') {
                    panel.style.maxHeight = `${panel.scrollHeight}px`;
                }
            });
        });
    });
}

initAccordions();


initPopups();
initStoriesGridVideo();

initPreloader().then(() => {
    initBannerImage();
    initInfoCenterText();
    initParallaxBlocks();
});